/**
 * AnalyticsService - Firebase Analytics tracking wrapper
 *
 * **Purpose**: Provides type-safe analytics event tracking with platform awareness
 *
 * **Key Features**:
 * - Type-safe event logging using AnalyticsEvent enum
 * - Screen view tracking for navigation analytics
 * - User property management for segmentation
 * - Platform-aware (web vs native) implementation
 * - Development mode logging for debugging
 *
 * **Platform Differences**:
 * - Web: Uses Firebase Analytics SDK (gtag)
 * - Native (future): Will use @capacitor-firebase/analytics plugin
 *
 * **Related Work Items**:
 * - WI-003: Analytics setup (Google Analytics on landing page)
 * - WI-033: FCM integration (Firebase setup)
 * - WI-039: Firebase Analytics integration (this implementation)
 *
 * **Privacy Compliance**:
 * - No PII logged in event parameters (use user IDs only)
 * - Events follow GDPR/CCPA privacy requirements
 * - Location coordinates NOT tracked (Phase 3 - V1.1 feature)
 *
 * @see analytics.types.ts - Event type definitions
 * @see CheckerFly_SDD.md Section 10.1 (Observability)
 */

import { Injectable, inject } from '@angular/core';
import {
  Analytics,
  logEvent,
  setUserId,
  setUserProperties,
  setAnalyticsCollectionEnabled,
} from '@angular/fire/analytics';
import { Platform } from '@ionic/angular/standalone';
import {
  AnalyticsEvent,
  AnalyticsEventParams,
  AnalyticsUserProperty,
  AnalyticsUserPropertyValue,
  ScreenViewParams,
} from '@core/types/analytics.types';
import { devLog, devError } from '@core';
import { environment } from '../../../environments/environment';

/**
 * localStorage key caching the analytics opt-out locally (WI-052).
 *
 * The canonical preference lives in Firestore (User.settings.analyticsEnabled)
 * for cross-device sync, but a local cache is required so an opted-out user is
 * NOT tracked in the window between app launch and the next Firestore read
 * (GDPR/CCPA). setEnabled() keeps both in sync.
 */
export const ANALYTICS_ENABLED_STORAGE_KEY = 'checkerfly-analytics-enabled';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private analytics = inject(Analytics);
  private platform = inject(Platform);
  private currentScreen: string | null = null;

  /**
   * User analytics opt-out state (WI-052, GDPR/CCPA).
   * Initialized from localStorage so a saved opt-out applies from the very
   * first event after launch. Default true (enabled) when nothing is stored.
   * The settings feature calls setEnabled() on toggle and when the Firestore
   * preference is loaded, keeping this cache in sync.
   */
  private enabled = this.readStoredEnabled();

  constructor() {
    // Apply a persisted opt-out at the Firebase SDK level immediately on
    // launch so automatic collection (e.g. page_view) is also stopped before
    // the Firestore preference is re-read (WI-052, GDPR/CCPA)
    if (environment.production && !this.enabled) {
      try {
        setAnalyticsCollectionEnabled(this.analytics, false);
      } catch (error: unknown) {
        devError('[AnalyticsService] Failed to apply stored opt-out:', error);
      }
    }
  }

  /**
   * Log a custom analytics event
   *
   * **CRITICAL**: This method is a no-op in development mode (!environment.production)
   * to prevent polluting production analytics data.
   *
   * @param eventName - Event name from AnalyticsEvent enum
   * @param params - Event parameters (type-safe based on event name)
   *
   * @example
   * ```typescript
   * this.analytics.logEvent(AnalyticsEvent.CHECKIN_CREATED, {
   *   checkin_id: 'abc123',
   *   trigger_time_offset: 60,
   *   contact_count: 2,
   * });
   * ```
   */
  logEvent(eventName: AnalyticsEvent | string, params?: AnalyticsEventParams | Record<string, unknown>): void {
    try {
      // Development logging only - don't send to Firebase in dev mode
      if (!environment.production) {
        devLog(`[Analytics] Event (dev mode, not sent): ${eventName}`, params);
        return;
      }

      // WI-052: Respect user analytics opt-out (GDPR/CCPA)
      if (!this.enabled) {
        return;
      }

      // Add timestamp if not provided
      const eventParams = {
        ...params,
        timestamp: params?.timestamp || new Date().toISOString(),
      };

      // Sanitize params to remove PII before logging
      const sanitizedParams = this.sanitizeParams(eventParams);

      // Log to Firebase Analytics
      // Cast eventName to string to satisfy Firebase Analytics API
      logEvent(this.analytics, eventName as string, sanitizedParams);

      devLog(`[Analytics] Event logged: ${eventName}`);
    } catch (error: unknown) {
      devError('[AnalyticsService] Failed to log event:', error);
    }
  }

  /**
   * Track screen view (navigation event)
   *
   * Should be called when user navigates to a new screen.
   * Automatically tracks previous screen for navigation flow analysis.
   *
   * @param screenName - Screen identifier (e.g., 'check-in-list', 'settings')
   *
   * @example
   * ```typescript
   * this.analytics.trackScreenView('check-in-create');
   * ```
   */
  trackScreenView(screenName: string): void {
    const params: ScreenViewParams = {
      screen_name: screenName,
      previous_screen: this.currentScreen || undefined,
      timestamp: new Date().toISOString(),
    };

    this.logEvent(AnalyticsEvent.SCREEN_VIEW, params);
    this.currentScreen = screenName;
  }

  /**
   * Set user ID for analytics tracking
   *
   * Should be called after successful authentication.
   * Enables cross-session user tracking and cohort analysis.
   *
   * **Privacy**: User ID is Firebase Auth UID, not phone number or email
   *
   * @param userId - Firebase Auth UID
   *
   * @example
   * ```typescript
   * this.analytics.setUserId('firebase-auth-uid-123');
   * ```
   */
  setUserId(userId: string | null): void {
    try {
      setUserId(this.analytics, userId);

      if (!environment.production) {
        devLog('[Analytics] User ID set:', userId);
      }
    } catch (error: unknown) {
      devError('[AnalyticsService] Failed to set user ID:', error);
    }
  }

  /**
   * Set user property for segmentation
   *
   * User properties persist across sessions and enable audience segmentation.
   *
   * **Common Use Cases**:
   * - Subscription tier (free/premium)
   * - Onboarding completion status
   * - Contact count (network size)
   * - Timezone (for timing analysis)
   *
   * @param property - Property name from AnalyticsUserProperty enum
   * @param value - Property value (string, number, or boolean)
   *
   * @example
   * ```typescript
   * this.analytics.setUserProperty(
   *   AnalyticsUserProperty.SUBSCRIPTION_TIER,
   *   'premium'
   * );
   * ```
   */
  setUserProperty(property: AnalyticsUserProperty, value: AnalyticsUserPropertyValue): void {
    try {
      setUserProperties(this.analytics, { [property]: value });

      if (!environment.production) {
        devLog(`[Analytics] User property set: ${property} = ${value}`);
      }
    } catch (error: unknown) {
      devError('[AnalyticsService] Failed to set user property:', error);
    }
  }

  /**
   * Set multiple user properties at once
   *
   * More efficient than calling setUserProperty multiple times.
   *
   * @param properties - Object with property names and values (partial record)
   *
   * @example
   * ```typescript
   * this.analytics.setUserProperties({
   *   [AnalyticsUserProperty.SUBSCRIPTION_TIER]: 'premium',
   *   [AnalyticsUserProperty.CONTACT_COUNT]: 3,
   *   [AnalyticsUserProperty.TIMEZONE]: 'America/Los_Angeles',
   * });
   * ```
   */
  setUserProperties(properties: Partial<Record<AnalyticsUserProperty, AnalyticsUserPropertyValue>>): void {
    try {
      setUserProperties(this.analytics, properties);

      if (!environment.production) {
        devLog('[Analytics] User properties set:', properties);
      }
    } catch (error: unknown) {
      devError('[AnalyticsService] Failed to set user properties:', error);
    }
  }

  /**
   * Track app open event
   *
   * Should be called when app initializes or resumes from background.
   *
   * @param fromNotification - Whether app was opened from a notification tap
   */
  trackAppOpen(fromNotification: boolean = false): void {
    this.logEvent(AnalyticsEvent.APP_OPEN, {
      from_notification: fromNotification,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Get current platform (for conditional analytics logic)
   *
   * @returns 'web', 'ios', or 'android'
   */
  getPlatform(): 'web' | 'ios' | 'android' {
    if (this.platform.is('ios')) {
      return 'ios';
    } else if (this.platform.is('android')) {
      return 'android';
    } else {
      return 'web';
    }
  }

  /**
   * Sanitize event params to remove PII
   *
   * **CRITICAL**: Never log PII (phone numbers, emails, notes, contact names).
   * This method strips known PII fields and fields matching PII patterns.
   *
   * @param params - Raw event params
   * @returns Params with PII removed
   *
   * @example
   * ```typescript
   * const sanitized = this.sanitizeParams({
   *   phone: '+15551234567', // REMOVED
   *   count: 5,              // KEPT
   * });
   * // Result: { count: 5 }
   * ```
   */
  private sanitizeParams(params: Record<string, unknown>): Record<string, unknown> {
    // PII fields to strip (WI-039 Section 2.1)
    const PII_FIELDS = [
      'phone', 'email', 'note', 'contactName', 'contactPhone',
      'phoneNumber', 'emailAddress', 'userName', 'displayName',
      'name', 'address', 'location'
    ];

    // Regex pattern for additional PII detection
    const PII_PATTERN = /phone|email|contact|name|address/i;

    const sanitized: Record<string, unknown> = {};

    for (const key of Object.keys(params)) {
      // Skip known PII fields (case-insensitive)
      if (PII_FIELDS.includes(key.toLowerCase())) {
        devLog(`[AnalyticsService] Stripped PII field: ${key}`);
        continue;
      }

      // Skip fields matching PII pattern
      if (PII_PATTERN.test(key)) {
        devLog(`[AnalyticsService] Stripped potential PII field: ${key}`);
        continue;
      }

      // Copy safe value
      const value = params[key];
      if (this.isValidParamValue(value)) {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  /**
   * Check if value is valid for Firebase Analytics
   *
   * Firebase Analytics only accepts string, number, boolean, or undefined values.
   *
   * @param value - Value to check
   * @returns True if value is valid for Firebase Analytics
   */
  private isValidParamValue(value: unknown): value is string | number | boolean | undefined {
    return (
      value === undefined ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    );
  }

  /**
   * Enable or disable analytics (for user opt-out)
   *
   * **WI-052**: Connected to persistence. The settings feature persists the
   * preference to Firestore (User.settings.analyticsEnabled) and calls this
   * method both when the user toggles the setting and when the saved
   * preference is loaded on app launch, so opt-out takes effect immediately
   * and survives restarts.
   *
   * **Note**: Analytics events are never sent in development mode; the state
   * is still tracked so behavior is consistent and testable.
   *
   * @param enabled - Whether to enable analytics
   *
   * @example
   * ```typescript
   * // User toggles analytics off in Settings
   * this.analytics.setEnabled(false);
   * ```
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;

    // Persist locally so the preference applies immediately on next launch,
    // before the Firestore copy is read (GDPR/CCPA - no tracking window)
    try {
      localStorage.setItem(ANALYTICS_ENABLED_STORAGE_KEY, String(enabled));
    } catch {
      // Storage unavailable (private mode / quota) - preference still applies
      // for this session and remains persisted in Firestore
      devLog('[AnalyticsService] Could not persist analytics preference locally');
    }

    // Apply at the Firebase SDK level (stops automatic collection too)
    // Only in production - the dev-mode Analytics instance is a bare mock
    if (environment.production) {
      try {
        setAnalyticsCollectionEnabled(this.analytics, enabled);
      } catch (error: unknown) {
        devError('[AnalyticsService] Failed to set collection state:', error);
      }
    }

    devLog('[AnalyticsService] Analytics enabled:', enabled);
  }

  /**
   * Whether analytics collection is currently enabled (user preference).
   *
   * @returns True unless the user has opted out via setEnabled(false)
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Read the locally cached analytics preference.
   * Defaults to true (enabled) when nothing is stored or storage fails.
   *
   * @returns False only when an explicit opt-out was persisted
   */
  private readStoredEnabled(): boolean {
    try {
      return localStorage.getItem(ANALYTICS_ENABLED_STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  }
}
